import React, { useEffect, useState } from 'react';
import {
    RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
    ResponsiveContainer, Legend,
} from 'recharts';
import { TrendingUp, TrendingDown, Minus, X } from 'lucide-react';
import { obtenerRankingValueScore, obtenerValueScoreDetalle } from './api/valueScore';
import CarLoader from './components/CarLoader';
import './ValueScoreRanking.css';

const CATEGORIAS_SEGMENTO = ['SUV', 'Pickup', 'Hatchback', 'Sedan', 'Trabajo', 'Citycar', 'Coupe', 'Deportivo', 'Convertible'];

const LABELS_CATEGORIA = { seguridad: 'Seguridad', tecnologia: 'Tecnología', motor: 'Motor', otros: 'Otros' };

function fmtMonto(n) {
    const v = Number(n);
    if (!Number.isFinite(v)) return '—';
    return `$${v.toLocaleString('es-CL')}`;
}

function badgeValueScore(score) {
    if (score == null) return null;
    if (score >= 110) return { texto: 'Subvalorado', clase: 'vs-badge--bueno', icon: <TrendingUp size={13} /> };
    if (score <= 90) return { texto: 'Sobrevalorado', clase: 'vs-badge--malo', icon: <TrendingDown size={13} /> };
    return { texto: 'En línea con el segmento', clase: 'vs-badge--neutro', icon: <Minus size={13} /> };
}

function DetalleVehiculo({ vehicleVersionId, onCerrar }) {
    const [detalle, setDetalle] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        obtenerValueScoreDetalle(vehicleVersionId)
            .then(setDetalle)
            .catch((e) => console.error(e))
            .finally(() => setLoading(false));
    }, [vehicleVersionId]);

    if (loading) return (
        <div className="vs-modal-overlay" onClick={onCerrar}>
            <div className="vs-modal" onClick={(e) => e.stopPropagation()}><CarLoader label="Cargando detalle…" /></div>
        </div>
    );
    if (!detalle?.score) return (
        <div className="vs-modal-overlay" onClick={onCerrar}>
            <div className="vs-modal" onClick={(e) => e.stopPropagation()}>
                <p className="vs-hint">Sin Value Score calculado para este modelo todavía.</p>
                <button className="ct-btn" onClick={onCerrar}><X size={13} /> Cerrar</button>
            </div>
        </div>
    );

    const { score, promedioSegmento, itemsFaltantesRelevantes } = detalle;
    const badge = badgeValueScore(score.valueScore);
    const desglose = score.desglose || {};
    const radarData = ['seguridad', 'tecnologia', 'motor', 'otros'].map((cat) => ({
        categoria: LABELS_CATEGORIA[cat],
        Propio: Math.round(desglose[cat]?.score ?? 0),
        'Promedio segmento': Math.round(promedioSegmento[cat] ?? 0),
    }));

    return (
        <div className="vs-modal-overlay" onClick={onCerrar}>
            <div className="vs-modal" onClick={(e) => e.stopPropagation()}>
                <button className="vs-modal-close" onClick={onCerrar}><X size={16} /></button>

                <div className="vs-modal-score">
                    <div className="vs-modal-score-num">{score.valueScore != null ? Math.round(score.valueScore) : '—'}</div>
                    {badge && <span className={`vs-badge ${badge.clase}`}>{badge.icon} {badge.texto}</span>}
                    <div className="vs-modal-score-hint">100 = promedio del segmento · fuente de ficha: {score.fuenteSpecs === 'exacta' ? 'exacta (esta versión)' : 'aproximada (mismo modelo)'}</div>
                </div>

                <div className="vs-modal-radar">
                    <ResponsiveContainer width="100%" height={260}>
                        <RadarChart data={radarData}>
                            <PolarGrid />
                            <PolarAngleAxis dataKey="categoria" tick={{ fontSize: 12 }} />
                            <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fontSize: 10 }} />
                            <Radar name="Propio" dataKey="Propio" stroke="#2563eb" fill="#2563eb" fillOpacity={0.35} />
                            <Radar name="Promedio segmento" dataKey="Promedio segmento" stroke="#94a3b8" fill="#94a3b8" fillOpacity={0.15} />
                            <Legend />
                        </RadarChart>
                    </ResponsiveContainer>
                </div>

                {itemsFaltantesRelevantes?.length > 0 && (
                    <div className="vs-modal-faltantes">
                        <div className="vs-modal-faltantes-title">Le falta esto, que la mayoría del segmento sí trae:</div>
                        {itemsFaltantesRelevantes.map((item) => (
                            <div key={item.key} className="vs-modal-faltante-item">
                                <span>{item.label}</span>
                                <span className="vs-modal-faltante-pct">{item.adopcionPct}% del segmento lo trae</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

export default function ValueScoreRanking() {
    const [categoria, setCategoria] = useState('SUV');
    const [ranking, setRanking] = useState([]);
    const [loading, setLoading] = useState(true);
    const [muestraInsuficiente, setMuestraInsuficiente] = useState(false);
    const [detalleId, setDetalleId] = useState(null);

    const cargar = () => {
        setLoading(true);
        obtenerRankingValueScore(categoria)
            .then((r) => { setRanking(r.filas || []); setMuestraInsuficiente(r.muestraInsuficiente); })
            .catch((e) => { console.error(e); setRanking([]); setMuestraInsuficiente(true); })
            .finally(() => setLoading(false));
    };

    useEffect(cargar, [categoria]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div className="vs-ranking-wrap">
            <div className="vs-ranking-toolbar">
                <span className="vs-ranking-label">Segmento:</span>
                <select className="vs-select" value={categoria} onChange={(e) => setCategoria(e.target.value)}>
                    {CATEGORIAS_SEGMENTO.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <span className="vs-ranking-hint">
                    Compara equipamiento vs precio dentro del mismo segmento — 100 es el promedio. Sobre 100: buen negocio para el cliente. Bajo 100: caro para lo que trae.
                </span>
            </div>

            {loading ? (
                <div className="vs-ranking-state"><CarLoader label="Calculando ranking…" /></div>
            ) : muestraInsuficiente ? (
                <div className="vs-ranking-state">
                    <p>Datos insuficientes en este segmento todavía — muy pocos modelos tienen ficha técnica enlazada.</p>
                </div>
            ) : ranking.length === 0 ? (
                <div className="vs-ranking-state"><p>Sin Value Score calculado para este segmento.</p></div>
            ) : (
                <div className="vs-ranking-table-wrap">
                    <table className="vs-ranking-table">
                        <thead>
                            <tr><th>#</th><th>Modelo</th><th>Precio</th><th>Score equipamiento</th><th>Value Score</th><th>Ficha</th></tr>
                        </thead>
                        <tbody>
                            {ranking.map((f) => {
                                const badge = badgeValueScore(f.valueScore);
                                return (
                                    <tr key={f.vehicleVersionId} onClick={() => setDetalleId(f.vehicleVersionId)} className="vs-ranking-row">
                                        <td>{f.posicion}</td>
                                        <td>{f.vehiculo ? `${f.vehiculo.marca} ${f.vehiculo.model} ${f.vehiculo.modelDetail}` : '—'}</td>
                                        <td>{fmtMonto(f.precio)}</td>
                                        <td>{f.scoreEquipamiento != null ? Math.round(f.scoreEquipamiento) : '—'}</td>
                                        <td className="vs-ranking-score">
                                            <strong>{Math.round(f.valueScore)}</strong>
                                            {badge && <span className={`vs-badge vs-badge--sm ${badge.clase}`}>{badge.texto}</span>}
                                        </td>
                                        <td className="vs-ranking-fuente">{f.fuenteSpecs === 'exacta' ? 'Exacta' : 'Aproximada'}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {detalleId && <DetalleVehiculo vehicleVersionId={detalleId} onCerrar={() => setDetalleId(null)} />}
        </div>
    );
}
